import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiArrowLeft, FiDownload, FiCheck, FiX, FiTrendingUp, 
  FiEye, FiSettings, FiSearch, FiPlus, FiMinus, FiRefreshCw,
  FiCheckSquare, FiMinusSquare
} = FiIcons;

export default function Comparison() {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    getProviderById, 
    terminMarktData,
    spotMarktData,
    renderValue, 
    selectedColumns, 
    updateSelectedColumn, 
    selectAllColumns, 
    clearAllColumns,
    columnDisplayNames
  } = useData();
  
  const [selectedIds, setSelectedIds] = useState([]);
  const [marktart, setMarktart] = useState('');
  const [energieart, setEnergieart] = useState('');
  const [providers, setProviders] = useState([]);
  
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSelectionExpanded, setIsSelectionExpanded] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ids = params.get('ids')?.split(',') || [];
    const markt = params.get('markt') || 'terminmarkt';
    const energie = params.get('energie') || 'strom';
    
    if (selectedIds.length === 0 && marktart === '') {
      setSelectedIds(ids);
      setMarktart(markt);
      setEnergieart(energie);
    }
  }, [location.search]);

  useEffect(() => {
    if (selectedIds.length > 0) {
      const providerData = selectedIds.map(id => getProviderById(id)).filter(Boolean);
      setProviders(providerData);
    } else {
      setProviders([]);
    }
  }, [selectedIds, getProviderById]);

  const updateUrl = (newIds, newMarkt, newEnergie) => {
    const idsParam = newIds.join(',');
    navigate(`/comparison?ids=${idsParam}&markt=${newMarkt}&energie=${newEnergie}`, { replace: true });
  };

  const handleMarktChange = (newMarkt) => {
    if (newMarkt === marktart) return;
    const currentProviders = selectedIds.map(id => getProviderById(id)).filter(Boolean);
    const targetData = newMarkt === 'terminmarkt' ? terminMarktData : spotMarktData;
    
    const newIds = [];
    currentProviders.forEach(p => {
      const match = targetData.find(np => 
        np.evu === p.evu && 
        (energieart === 'strom' ? np.bietet_strom : np.bietet_gas)
      );
      if (match) newIds.push(match.id);
    });

    setMarktart(newMarkt);
    setSelectedIds(newIds);
    updateUrl(newIds, newMarkt, energieart);
  };

  const handleEnergieChange = (newEnergie) => {
    if (newEnergie === energieart) return;
    setEnergieart(newEnergie);
    updateUrl(selectedIds, marktart, newEnergie);
  };

  const toggleProvider = (id) => {
    let newIds;
    if (selectedIds.includes(id)) {
      newIds = selectedIds.filter(pid => pid !== id);
    } else {
      newIds = [...selectedIds, id];
    }
    setSelectedIds(newIds);
    updateUrl(newIds, marktart, energieart);
  };

  const getAvailableProviders = () => {
    const sourceData = marktart === 'terminmarkt' ? terminMarktData : spotMarktData;
    return sourceData.filter(p => {
      const matchesEnergie = energieart === 'strom' ? p.bietet_strom : p.bietet_gas;
      const matchesSearch = searchTerm === '' || p.evu.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesEnergie && matchesSearch;
    });
  };

  const availableProviders = getAvailableProviders();

  const handleSelectAllAvailable = () => {
    const newIds = [...new Set([...selectedIds, ...availableProviders.map(p => p.id)])];
    setSelectedIds(newIds);
    updateUrl(newIds, marktart, energieart);
  };

  const handleDeselectAllAvailable = () => {
    setSelectedIds([]);
    updateUrl([], marktart, energieart);
  };

  const getComparisonColumns = () => {
    const columns = [];
    if (selectedColumns.evu) columns.push({ key: 'evu', label: 'EVU', alwaysShow: true });

    if (energieart === 'strom') {
      if (selectedColumns.strom_slp) columns.push({ key: 'strom_slp', label: 'Strom SLP' });
      if (selectedColumns.strom_rlm) columns.push({ key: 'strom_rlm', label: 'Strom RLM' });
      if (selectedColumns.verbrauchsgrenzen_strom) columns.push({ key: 'verbrauchsgrenzen_strom', label: 'Verbrauchsgrenzen' });
    } else {
      if (selectedColumns.gas_slp) columns.push({ key: 'gas_slp', label: 'Gas SLP' });
      if (selectedColumns.gas_rlm) columns.push({ key: 'gas_rlm', label: 'Gas RLM' });
      if (selectedColumns.verbrauchsgrenzen_gas) columns.push({ key: 'verbrauchsgrenzen_gas', label: 'Verbrauchsgrenzen' });
    }

    if (selectedColumns.bestellungsrelevante_unterlagen) columns.push({ key: 'bestellungsrelevante_unterlagen', label: 'Bestellungsunterlagen' });
    if (selectedColumns.pflichtangaben) columns.push({ key: 'pflichtangaben', label: 'Pflichtangaben' });
    if (selectedColumns.provisionsgrenze) columns.push({ key: 'provisionsgrenze', label: 'Provisionsgrenze' });
    if (selectedColumns.auszahlung) columns.push({ key: 'auszahlung', label: 'Auszahlung' });
    if (selectedColumns.vertragsunterlagen_nach_abschluss) columns.push({ key: 'vertragsunterlagen_nach_abschluss', label: 'Vertragsunterlagen' });
    if (selectedColumns.ausgeschlossene_branchen) columns.push({ key: 'ausgeschlossene_branchen', label: 'Ausgeschlossene Branchen' });
    if (selectedColumns.bemerkung) columns.push({ key: 'bemerkung', label: 'Bemerkung' });
    if (selectedColumns.bonitaetsgrenze) columns.push({ key: 'bonitaetsgrenze', label: 'Bonitätsgrenze' });
    if (selectedColumns.buendelung) columns.push({ key: 'buendelung', label: 'Bündelung' });
    if (selectedColumns.vollmachten_erlaubt) columns.push({ key: 'vollmachten_erlaubt', label: 'Vollmachten' });

    // WICHTIG: Diese Spalten NUR bei Terminmarkt anzeigen!
    if (marktart === 'terminmarkt') {
        if (selectedColumns.mehr_mindermengen_in_prozent) columns.push({ key: 'mehr_mindermengen_in_prozent', label: 'Mehr-/Mindermengen in %' });
        if (selectedColumns.kuendigungsfrist) columns.push({ key: 'kuendigungsfrist', label: 'Kündigungsfrist' });
        if (selectedColumns.verlaengerung_aktiv_passiv) columns.push({ key: 'verlaengerung_aktiv_passiv', label: 'Verlängerung' });
    }

    if (marktart === 'spotmarkt' && selectedColumns.wechsel_von_spot_zu_fest) columns.push({ key: 'wechsel_von_spot_zu_fest', label: 'Wechsel Spot→Fest' });

    return columns;
  };

  const comparisonColumns = getComparisonColumns();

  const exportComparison = () => {
    const csvContent = [
      ['Kriterium', ...providers.map(p => p.evu)],
      ...comparisonColumns.map(col => [col.label, ...providers.map(provider => renderValue(provider[col.key] || ''))])
    ].map(row => row.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Vergleich_${marktart}_${energieart}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleColumnToggle = (column) => updateSelectedColumn(column, !selectedColumns[column]);

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-4">
          <Link to="/" className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Zurück
          </Link>
          <h2 className="text-2xl font-bold text-[#1e314b]">Anbietervergleich</h2>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => setShowColumnSelector(!showColumnSelector)} className="inline-flex items-center px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <SafeIcon icon={FiSettings} className="mr-2" />
            Vergleichskriterien anpassen
          </button>
          <button onClick={exportComparison} className="inline-flex items-center px-4 py-2 bg-[#1e314b] text-white rounded-lg hover:bg-[#152738] transition-colors">
            <SafeIcon icon={FiDownload} className="mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Column Selector */}
      <AnimatePresence>
        {showColumnSelector && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-white border border-gray-200 rounded-xl p-6 overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-[#1e314b]">Angezeigte Kriterien</h3>
              <div className="space-x-2">
                <button onClick={selectAllColumns} className="text-xs px-2 py-1 bg-[#1e314b] text-white rounded">Alle anzeigen</button>
                <button onClick={clearAllColumns} className="text-xs px-2 py-1 border border-gray-300 rounded">Alle verbergen</button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(columnDisplayNames).map(([key, label]) => (
                <label key={key} className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                  <input type="checkbox" checked={selectedColumns[key]} onChange={() => handleColumnToggle(key)} className="w-4 h-4 text-[#9b061b] border-gray-300 rounded focus:ring-[#9b061b]" />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Sidebar: Provider Selection */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center cursor-pointer" onClick={() => setIsSelectionExpanded(!isSelectionExpanded)}>
              <h3 className="font-semibold text-[#1e314b]">Vergleich anpassen</h3>
              <SafeIcon icon={isSelectionExpanded ? FiMinus : FiPlus} />
            </div>
            
            <AnimatePresence>
              {isSelectionExpanded && (
                <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="p-4 space-y-6">
                  {/* Market & Energy Type */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Markt</label>
                      <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                        <button onClick={() => handleMarktChange('terminmarkt')} className={`flex-1 py-2 text-sm font-medium ${marktart === 'terminmarkt' ? 'bg-[#1e314b] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Termin</button>
                        <button onClick={() => handleMarktChange('spotmarkt')} className={`flex-1 py-2 text-sm font-medium ${marktart === 'spotmarkt' ? 'bg-[#1e314b] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Spot</button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Energie</label>
                      <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                        <button onClick={() => handleEnergieChange('strom')} className={`flex-1 py-2 text-sm font-medium ${energieart === 'strom' ? 'bg-[#9b061b] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Strom</button>
                        <button onClick={() => handleEnergieChange('gas')} className={`flex-1 py-2 text-sm font-medium ${energieart === 'gas' ? 'bg-[#9b061b] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Gas</button>
                      </div>
                    </div>
                  </div>

                  {/* Provider Search & List */}
                  <div>
                    <div className="relative mb-3">
                      <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input type="text" placeholder="Versorger suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-[#1e314b] focus:border-[#1e314b]" />
                    </div>
                    
                    <div className="flex space-x-2 mb-3">
                       <button onClick={handleSelectAllAvailable} className="flex-1 flex items-center justify-center px-2 py-1.5 border border-gray-300 rounded text-xs hover:bg-gray-50 text-gray-700">
                          <SafeIcon icon={FiCheckSquare} className="mr-1" />Alle
                       </button>
                       <button onClick={handleDeselectAllAvailable} className="flex-1 flex items-center justify-center px-2 py-1.5 border border-gray-300 rounded text-xs hover:bg-gray-50 text-gray-700">
                          <SafeIcon icon={FiMinusSquare} className="mr-1" />Keine
                       </button>
                    </div>

                    <div className="max-h-60 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                      {availableProviders.map(p => (
                        <label key={p.id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50 cursor-pointer group">
                          <div className="flex items-center space-x-2 overflow-hidden">
                            <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleProvider(p.id)} className="w-4 h-4 text-[#9b061b] border-gray-300 rounded focus:ring-[#9b061b]" />
                            <span className="text-sm text-gray-700 truncate group-hover:text-gray-900">{p.evu}</span>
                          </div>
                          {selectedIds.includes(p.id) && <SafeIcon icon={FiCheck} className="text-[#9b061b] w-3 h-3 flex-shrink-0" />}
                        </label>
                      ))}
                      {availableProviders.length === 0 && <p className="text-sm text-gray-500 text-center py-2">Keine Versorger gefunden</p>}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="lg:col-span-9">
          {providers.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden overflow-x-auto">
              <table className="w-full min-w-max">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-48 sticky left-0 bg-gray-50 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                      Kriterium
                    </th>
                    {providers.map(provider => (
                      <th key={provider.id} className="px-6 py-4 text-left relative min-w-[200px]">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="text-lg font-bold text-[#1e314b] mb-1">{provider.evu}</div>
                            <div className="text-xs text-gray-500 font-normal">{provider.bietet_strom && 'Strom'} {provider.bietet_strom && provider.bietet_gas && '+'} {provider.bietet_gas && 'Gas'}</div>
                          </div>
                          <button onClick={() => toggleProvider(provider.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1">
                            <SafeIcon icon={FiX} />
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {comparisonColumns.map((column, idx) => (
                    <tr key={column.key} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] bg-inherit">
                        {column.label}
                      </td>
                      {providers.map(provider => (
                        <td key={`${provider.id}-${column.key}`} className="px-6 py-4 text-sm text-gray-700">
                          {renderValue(provider[column.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <SafeIcon icon={FiTrendingUp} className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Versorger ausgewählt</h3>
              <p className="text-gray-500">Bitte wählen Sie Versorger aus der Liste links aus, um einen Vergleich zu starten.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}